/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */
import InteractionsService from '../../service/interactions';
import WyUtils from '../../library/wy-utils.js';
import WyFilter from '../../library/wy-filter.js';
import moment from 'moment';

const state = {
  contactReasonRunStats: null,
  transcriptInfoReference: null,
};

const getters = {
  contactReasonRunStats: (state) => state.contactReasonRunStats,
  transcriptInfoReference: (state) => state.transcriptInfoReference,
};

const mutations = {
  updateTranscriptInfoReference(state, data) {
    if (data) {
      state.transcriptInfoReference = data;
    } else {
      state.transcriptInfoReference = null;
    }
  },
};

const actions = {
  fetchChatTopics({ getters }, options) {
    let payload = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel), options.dateRanges);

    if (getters.dataModel.filters.topics && getters.dataModel.filters.topics.value) {
      payload[getters.dataModel.filters.topics.name] = getters.dataModel.filters.topics.value.reduce((list, item) => {
        item.mergedTopics.map((el) => list.push(el));
        return list;
      }, []);
    }
    payload.topics = []; //Always fetch date without topics filter
    if (options && options.maxTopics) payload.maxTopics = options.maxTopics;
    return InteractionsService.fetchChatTopics(getters.authUser, payload);
  },

  async fetchChatFilters({ getters, dispatch, rootState }, { pageName }) {
    return new Promise(async (resolve) => {
      const dataModel = rootState.site.pages[pageName].dataModel;

      if (!getters.localStorage.agentData) {
        dataModel.filters = {
          agentGroups: { label: 'Group', name: 'agentGroups', value: null, type: 'multiSelectCheckbox', placeholder: 'Search agent groups' },
          agentTeams: { label: 'Team', name: 'agentTeams', value: null, type: 'multiSelectCheckbox', placeholder: 'Search teams' },
          agents: { label: 'Agent', name: 'agents', value: null, type: 'multiSelectCheckbox', placeholder: 'Search agents', isRequire: true },
          languages: { label: 'Language', name: 'languages', value: null, type: 'multiSelectCheckbox', placeholder: 'Search languages', category: 'moreFilters' },
          tasks: { label: 'Tasks', name: 'tasks', value: null, type: 'multiSelectCheckbox', placeholder: 'Search tasks' },
          topics: { label: 'Topics', name: 'topics', value: null, type: 'hidden' },
          directions: { label: 'Direction', name: 'directions', value: '', type: 'checkbox', isRequire: true, category: 'moreFilters' },
          channels: { label: 'Channels', name: 'channels', value: null, type: 'multiSelectCheckbox', placeholder: 'Search channels', category: 'moreFilters' },
          contactTypes: { label: 'Contact Types', name: 'contactTypes', value: null, type: 'multiSelectCheckbox', placeholder: 'Search contact types' },
        };

        let res = await InteractionsService.fetchChatFilters(getters.authUser);
        if (res && res.status == 200 && res.data) {
          await dispatch('updateLocalStorage', { key: 'agentData', data: {
            agentGroups: res.data.agentGroups,
            agentTeams: res.data.agentTeams,
            agents: res.data.agents,
          }});

          // date limits
          if (res.data.dateLimits && res.data.dateLimits.length > 0) {
            dataModel.dateMin = res.data.dateLimits[0].firstDate;
            dataModel.dateMax = res.data.dateLimits[0].lastDate;
          }

          // agentGroups list
          if (dataModel.filters.agentGroups) {
            dataModel.filters.agentGroups.dataSource = (res.data.agentGroups || []).map((item) => {
              return { key: item.groupId, label: item.groupName };
            });
            dataModel.filters.agentGroups.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            dataModel.filters.agentGroups.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // agentTeams list
          if (dataModel.filters.agentTeams) {
            dataModel.filters.agentTeams.dataSource = (res.data.agentTeams || []).map((item) => {
              return { key: item.teamId, label: item.teamName, groupId: item.groupId };
            });
            dataModel.filters.agentTeams.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            dataModel.filters.agentTeams.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // agents list
          if (dataModel.filters.agents) {
            dataModel.filters.agents.dataSource = (res.data.agents || []).map((item) => {
              return { key: item.agentId, label: item.agentName, teamId: item.teamId, groupId: item.groupId };
            });
            dataModel.filters.agents.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            dataModel.filters.agents.dataSource.unshift({ key: 'all', label: 'All' });
          }
          // languages list
          //mockdata: 
          // res.data.languages = [
          //   {languageId: 'en', languageName: 'English'},
          //   {languageId: 'fr', languageName: 'French'}
          // ]
          if (dataModel.filters.languages) {
            dataModel.filters.languages.dataSource = (res.data.languages || []).map((item) => {
              return { key: item.languageId, label: item.languageName };
            });
            dataModel.filters.languages.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            if(dataModel.filters.languages.dataSource.length > 1) {
              dataModel.filters.languages.dataSource.unshift({ key: 'all', label: 'All' });
            }
            else if(dataModel.filters.languages.dataSource.length == 1){
              // dataModel.filters.languages.value = [dataModel.filters.languages.dataSource[0].key];
              dataModel.filters.languages.value = [];
            }
            else {
              dataModel.filters.languages.value = [];
            }
          }
          // task list
          if (dataModel.filters.tasks) {
            dataModel.filters.tasks.dataSource = (res.data.tasks || []).map((item) => {
              return { key: item.taskId, label: item.taskName };
            });
            dataModel.filters.tasks.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            dataModel.filters.tasks.dataSource.unshift({ key: 'all', label: 'All' });
          }
          // direction list
          //mockdata: 
          res.data.directions = [
            {
              "direction": "inbound"
            },
            {
              "direction": "outbound"
            }
          ]
          if (dataModel.filters.directions) {
            const directionsMap = getters.authUser.config.app.configs.directionsMapping || {};
            // const directionsMap = getters.authUser.config.app.configs.directionsMapping || {'all': "All"};
            dataModel.filters.directions.dataSource = (res.data.directions || []).map((item) => {
              return { key: item.direction, label: directionsMap[item.direction] && directionsMap[item.direction] != '' ? directionsMap[item.direction] : WyUtils.toCamelCase(item.direction) };
            });
            dataModel.filters.directions.dataSource.unshift({ key: '', label: 'All' });
            dataModel.filters.directions.value = '';
          }

          if (dataModel.filters.contactTypes) {
            dataModel.filters.contactTypes.dataSource = (res.data.contactTypes || []).map((item) => {
              return { key: item.contactTypeKey, label: item.contactTypeName };
            });
            dataModel.filters.contactTypes.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            if(dataModel.filters.contactTypes.dataSource.length > 1) {
              dataModel.filters.contactTypes.dataSource.unshift({ key: 'all', label: 'All' });
            }
            else if(dataModel.filters.contactTypes.dataSource.length == 1){
              // dataModel.filters.contactTypes.value = [dataModel.filters.contactTypes.dataSource[0].key];
              dataModel.filters.contactTypes.value = [];
            }
          }

          if (dataModel.filters.channels) {
            dataModel.filters.channels.dataSource = (res.data.channels || []).map((item) => {
              return { key: item.channelKey, label: item.channelName };
            });
            dataModel.filters.channels.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            if(dataModel.filters.channels.dataSource.length > 1) {
              dataModel.filters.channels.dataSource.unshift({ key: 'all', label: 'All' });
            }
            // comment out this due to this ticket issue  1078014
            // else if(dataModel.filters.channels.dataSource.length == 1){
            //   // dataModel.filters.channels.value = [dataModel.filters.channels.dataSource[0].key];
            //   dataModel.filters.channels.value = '';
            // }
          }

          // set iframe selected filters
          WyFilter.setIFrameSelectedFilters(dataModel);

          // reset appliedFiltersCounter to 0
          dataModel.appliedFiltersCounter = 0;

          await dispatch('initFilterOriginalSource');
          resolve();
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    });
  },

  fetchChatPerformance({ getters }, { dateRanges, topics }) {
    let payload = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel), dateRanges);
    if (topics) {
      payload.topicDisplayNames = topics.map((item) => item.key);
      payload[getters.dataModel.filters.topics.name] = topics.reduce((list, item) => {
        item.mergedTopics.map((el) => list.push(el));
        return list;
      }, []);
    }
    return InteractionsService.fetchChatPerformance(getters.authUser, payload);
  },

  async fetchContactReasons({ commit, getters }, options) {
    if (getters.dataModel.dateStart && getters.dataModel.dateEnd) {
      let results = [];

      let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
      let res = await InteractionsService.fetchContactReasons(getters.authUser, options, payload);

      if (getters.moreFiltersChanged <= 0) {
        if (getters.dataModel.dateHasChanged) {
          getters.dataModel.dateHasChanged = false;
        }
      }

      // if not cached, process the following
      if (!(res === true)) {
        if (res && res.status === 200 && res.data.results) {
          let resultsA = res.data.results;
          //let resultsB = res.data.resultsCompare;
          let sumTotalVolume = resultsA.reduce((total, item) => {
            return total + item.totalVolume;
          }, 0);
          let resultsAObj = {};
          
          resultsA.forEach((item) => {
            resultsAObj[item.topicDisplayName] = item;
          });
          Object.keys(resultsAObj).forEach((key) => {
            let item = resultsAObj[key];
            item.isSelected = false;
            item.isEditing = false;
            item.isEditable = !item.isOthers;

            if (item.mergedTopics && item.mergedTopics.length > 1) {
              item.topicDisplayNameWithCount = item.topicDisplayName + ` (${item.mergedTopics.length})`; // showing number of merged topics
            } else {
              item.topicDisplayNameWithCount = item.topicDisplayName;
            }

            item.numOfDaysInRange = getters.dataModel.dateRangeInDays;
            item.volumePercent = ((item.totalVolume / sumTotalVolume) * 100).toFixed(2); // TODO: calculate the percent
            item.volumePercentNum = (item.totalVolume / sumTotalVolume) * 100;
            generateContactReasonsData(item, getters.xnumber);
            generatAlertIconData(item);

            results.push(item);
          });
          let totalTopics = 0;
          if (res.data.totalTopics) {
            totalTopics = res.data.totalTopics;
          }
          commit('updateTableModelResult', {
            results: results,
            totalRecs: results.length,
            totalNumber: totalTopics,
            totalPages: 1,
          });

          return true;
        }

        state.contactReasonRunStats = null;
        return false;
      } else {
        return true;
      }
    }
  },

  fetchAllTopics({ getters }, payload) {
    return InteractionsService.fetchAllTopics(getters.authUser, payload);
  },

  saveTopicsToTask({ getters }, payload) {
    if (payload && payload.topicList) {
      return InteractionsService.saveTopicsToTask(getters.authUser, payload);
    }
    return null;
  },

  mergeInteractionContactReasonsTopics({ getters }, payload) {
    if (payload && payload.topicDisplayName) {
      return InteractionsService.mergeContactReasonsTopics(getters.authUser, payload);
    }
    return null;
  },

  unmergeContactReasons({ getters }, payload) {
    if (payload && payload.topicDisplayName && payload.topicsToUnmerge && payload.topicsToUnmerge.length > 0 && payload.taskId) {
      return InteractionsService.mergeContactReasonsTopics(getters.authUser, payload);
    }
    return null;
  },

  async updateTopicName({ getters }, topicNameObject) {
    if (topicNameObject && topicNameObject.newTopicDisplayName && topicNameObject.newTopicDisplayName.trim() != ''&&topicNameObject.taskId) {
      return InteractionsService.updateTopicName(getters.authUser, topicNameObject);
    }
    return null;
  },

  fetchContactReasonsTopicTrend({ getters }, {item}) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = item.topicDisplayName;
    payload.topics = item.mergedTopics;
    payload.isOthers = item.isOthers;

    return InteractionsService.fetchContactReasonsTopicTrend(getters.authUser, payload);
  },

  fetchContactReasonsTopicEntities({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;

    return InteractionsService.fetchContactReasonsTopicEntities(getters.authUser, payload);
  },

  fetchTranscriptSearchTimeRange({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload = {
      ...payload,
      ...data
    }

    return InteractionsService.fetchTranscriptSearchTimeRange(getters.authUser, payload);
  },

  fetchTranscriptSummary({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.conversationId = data.conversationId;
    return InteractionsService.fetchTranscriptSummary(getters.authUser, payload);
  },

  async fetchTranscriptSearch({ getters }, data) {
    delete data.searchActivatedBy;

    const transcriptFilters = JSON.parse(JSON.stringify(data.transcriptSearchFilter));
    transcriptFilters.searchByType = "Empty";
    transcriptFilters.agentGroups = transcriptFilters.agentGroups || [];
    transcriptFilters.agentIds = transcriptFilters.agentIds || [];
    transcriptFilters.agentQueues = transcriptFilters.agentQueues || [];
    transcriptFilters.conversationTypes = transcriptFilters.conversationTypes || [];
    transcriptFilters.sentiment = transcriptFilters.sentiment || [];
    transcriptFilters.BES_filter_is_on = transcriptFilters.besSignals &&  transcriptFilters.besSignals.length > 0 || false,
    transcriptFilters.BAS_filter_is_on = transcriptFilters.basSignals  &&  transcriptFilters.basSignals.length > 0 || false,
    transcriptFilters.BES_signals = transcriptFilters.besSignals || [],
    transcriptFilters.BAS_signals  = transcriptFilters.basSignals || [],
    delete transcriptFilters.action;
    delete transcriptFilters.handOffsMax;
    delete transcriptFilters.handOffsMin;
    delete transcriptFilters.searchType;
    delete transcriptFilters.handleTimeMillisMax;
    delete transcriptFilters.handleTimeMillisMin;
    delete transcriptFilters.responseTimeMillisMax;
    delete transcriptFilters.responseTimeMillisMin;

    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = data.topicDisplayName;
    payload.topics = data.topics;
    // payload.transcriptFilters = data.transcriptFilters
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
          transcriptFilters.searchByType = "Conversation ID Search";
        }
      }
    } 
    else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;
        transcriptFilters.searchByType = "Keyword Search";
      }
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;
    // payload.savedTranscript = data.savedTranscript;
    if (data.isOthers != undefined) payload.isOthers = data.isOthers;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';
    payload.fromContactReasonsDetail = data.contactReason ? true : false;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    let res = await InteractionsService.fetchTranscriptSearch(getters.authUser, payload);

    if (res && res.status == 200 && res.data) {
      return res
    }
    else {
      return null
    }
  },

  async fetchTopicDisplayNameList({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') payload.textQuery = data.transcriptSearchFilter.searchKeyword;
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    return InteractionsService.fetchTopicDisplayNameList(getters.authUser, payload);
  },


  exportTranscriptSearch({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') payload.textQuery = data.transcriptSearchFilter.searchKeyword;
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;
    if (data.isOthers != undefined) payload.isOthers = data.isOthers

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }

    return InteractionsService.exportTranscriptSearch(getters.authUser, payload);
  },

  addTranscript({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    if (data.conversationId) payload.conversationId = data.conversationId;

    return InteractionsService.addTranscript(getters.authUser, payload);
  },

  removeTranscript({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    if (data.conversationId) payload.conversationId = data.conversationId;
    return InteractionsService.removeTranscript(getters.authUser, payload);
  },

  fetchContactReasonsGroupedTopicConversations({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload = {
      ...payload,
      ...data
    }
    return InteractionsService.fetchContactReasonsGroupedTopicConversations(getters.authUser, payload);
  },

  fetchContactReasonsRelatedTopicConversations({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload = {
      ...payload,
      ...data
    }
    return InteractionsService.fetchContactReasonsRelatedTopicConversations(getters.authUser, payload);
  },

  fetchTopicsByConfidenceScore({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload = {
      ...payload,
      ...data
    }

    return InteractionsService.fetchTopicsByConfidenceScore(getters.authUser, payload);
  },

  fetchContactReasonsTopicConversationDetail({ getters }, payload) {
    return InteractionsService.fetchContactReasonsTopicConversationDetail(getters.authUser, payload);
  },

  fetchMetrics({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    return InteractionsService.fetchMetrics(getters.authUser, payload);
  },

  fetchRelatedTopics({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload = {
      ...payload,
      ...data
    }
    
    return InteractionsService.fetchRelatedTopics(getters.authUser, payload);
  },
};

function convertToChangeValue(changeValue) {
  if (changeValue > 0) return '+';
  else if (changeValue < 0) return '-';
  else return '';
}

function generateContactReasonsData(item, xnumber) {
  let noneDataSorting = -1000000;

  // volume
  item.totalVolumeDMValue = item.totalVolume != null ? item.totalVolume * xnumber : item.totalVolume;
  if (item.totalVolumeCompare) {
    item.totalVolumeCompareDMValue = item.totalVolumeCompare != null ? item.totalVolumeCompare * xnumber : item.totalVolumeCompare;
    item.volumeChangeWithoutAbs = (item.totalVolume - item.totalVolumeCompare) / item.totalVolumeCompare;
    item.volumeChange = Math.abs(item.volumeChangeWithoutAbs);
    item.volumeChangeDisplay = convertToChangeValue(item.volumeChangeWithoutAbs) + (item.volumeChange * 100).toFixed(2);
    item.volumeChangePercent = (item.volumeChange * 100).toFixed(2);
    item.volumeChangeTooltip = generatTooltipsMsg(item.totalVolumeCompare != null ? item.totalVolumeCompare * xnumber : item.totalVolumeCompare, item.numOfDaysInRange, false);
  } else {
    item.volumeChange = null;
    item.volumeChangeWithoutAbs = noneDataSorting;
    item.volumeChangeDisplay = null;
  }

  //  avg auto eval score
  item.avgAutoEvalScoreDisplay = item.avgAutoEvalScore != null ? item.avgAutoEvalScore : item.avgAutoEvalScore;
  if (item.avgAutoEvalScoreCompare) {
    item.avgAutoEvalScoreCompareDisplay = item.avgAutoEvalScoreCompare != null ? item.avgAutoEvalScoreCompare : item.avgAutoEvalScoreCompare;
    item.avgAutoEvalScoreChangeWithoutAbs = (item.avgAutoEvalScore - item.avgAutoEvalScoreCompare) / item.avgAutoEvalScoreCompare;
    item.avgAutoEvalScoreChange = Math.abs(item.avgAutoEvalScoreChangeWithoutAbs);
    item.avgAutoEvalScoreChangeDisplay = convertToChangeValue(item.avgAutoEvalScoreChangeWithoutAbs) + (item.avgAutoEvalScoreChange * 100).toFixed(2);
    item.avgAutoEvalScoreChangePercent = (item.avgAutoEvalScoreChange * 100).toFixed(2);
    item.avgAutoEvalScoreChangeTooltip = generatTooltipsMsg(item.avgAutoEvalScoreCompare != null ? item.avgAutoEvalScoreCompare : item.avgAutoEvalScoreCompare, item.numOfDaysInRange, false);
  } else {
    item.avgAutoEvalScoreChange = null;
    item.avgAutoEvalScoreChangeWithoutAbs = noneDataSorting;
    item.avgAutoEvalScoreChangeDisplay = null;
  }

  // agent handle time
  item.avgAgentHandleTimeDisplay = item.avgAgentHandleTime >= 0 ? WyUtils.toNiceTimeFormat(item.avgAgentHandleTime * 60) : item.avgAgentHandleTime;
  if (item.avgAgentHandleTimeCompare && item.avgAgentHandleTimeCompare > -1) {
    item.avgAgentHandleTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgAgentHandleTimeCompare * 60);
    item.avgAgentHandleTimeChangeWithoutAbs = (item.avgAgentHandleTime - item.avgAgentHandleTimeCompare) / item.avgAgentHandleTimeCompare;
    item.avgAgentHandleTimeChange = Math.abs(item.avgAgentHandleTimeChangeWithoutAbs);
    item.avgAgentHandleTimeChangeDisplay = convertToChangeValue(item.avgAgentHandleTimeChangeWithoutAbs) + (item.avgAgentHandleTimeChange * 100).toFixed(2);
    item.avgAgentHandleTimeChangePercent = (item.avgAgentHandleTimeChange * 100).toFixed(2);
    item.avgAgentHandleTimeChangeTooltip = generatTooltipsMsg(item.avgAgentHandleTimeCompareDisplay, item.numOfDaysInRange, false);
  } else {
    item.avgAgentHandleTimeCompareDisplay = null;
    item.avgAgentHandleTimeChange = null;
    item.avgAgentHandleTimeChangeWithoutAbs = noneDataSorting;
    item.avgAgentHandleTimeChangeDisplay = null;
  }

  // avg response time
  item.avgAgentResponseTimeDisplay = WyUtils.toNiceTimeFormat(item.avgAgentResponseTime * 60);
  if (item.avgAgentResponseTimeCompare) {
    item.avgAgentResponseTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgAgentResponseTimeCompare * 60);
    item.avgAgentResponseTimeChangeWithoutAbs = (item.avgAgentResponseTime - item.avgAgentResponseTimeCompare) / item.avgAgentResponseTimeCompare;
    item.avgAgentResponseTimeChange = Math.abs(item.avgAgentResponseTimeChangeWithoutAbs);
    item.avgAgentResponseTimeChangeDisplay = convertToChangeValue(item.avgAgentResponseTimeChangeWithoutAbs) + (item.avgAgentResponseTimeChange * 100).toFixed(2);
    item.avgAgentResponseTimeChangePercent = (item.avgAgentResponseTimeChange * 100).toFixed(2);
    item.avgAgentResponseTimeChangeTooltip = generatTooltipsMsg(item.avgAgentResponseTimeCompareDisplay, item.numOfDaysInRange, false);
    item.avgAgentResponseTimeChangeWithoutAbsNum = parseFloat(item.avgAgentResponseTimeChangeWithoutAbs);
  } else {
    item.avgAgentResponseTimeCompareDisplay = null;
    item.avgAgentResponseTimeChange = null;
    item.avgAgentResponseTimeChangeWithoutAbs = noneDataSorting;
    item.avgAgentResponseTimeChangeWithoutAbsNum = noneDataSorting;
    item.avgAgentResponseTimeChangeDisplay = null;
  }

  // median response time
  item.medianResponseTimeDisplay = WyUtils.toNiceTimeFormat(item.medianResponseTime * 60);
  if (item.medianResponseTimeCompare) {
    item.medianResponseTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.medianResponseTimeCompare * 60);
    item.medianResponseTimeChangeWithoutAbs = (item.medianResponseTime - item.medianResponseTimeCompare) / item.medianResponseTimeCompare;
    item.medianResponseTimeChange = Math.abs(item.medianResponseTimeChangeWithoutAbs);
    item.medianResponseTimeChangeDisplay = convertToChangeValue(item.medianResponseTimeChangeWithoutAbs) + (item.medianResponseTimeChange * 100).toFixed(2);
    item.medianResponseTimeChangeTooltip = generatTooltipsMsg(item.medianResponseTimeCompare, item.numOfDaysInRange, false);
  } else {
    item.medianResponseTimeCompareDisplay = null;
    item.medianResponseTimeChange = null;
    item.medianResponseTimeChangeWithoutAbs = noneDataSorting;
    item.medianResponseTimeChangeDisplay = null;
  }
}

function generatAlertIconData(item) {
  item.alertsIcon = {
    icon: '',
    value: 0,
  };
  // item.dateFirstFound = '2020-11-10';
  if (item.dateFirstFound && item.dateFirstFound != '') {
    item.dateFirstFoundSorting = moment(item.dateFirstFound, 'YYYY-MM-DD').format('X');
    item.dateFirstFound = WyUtils.formatDate(item.dateFirstFound);
  }
}

function generatTooltipsMsg(compareValue, numOfDaysInRange, isPercentage) {
  let message = '';
  let percentage = '';
  if (isPercentage) {
    percentage = '%';
  }
  message = `Compared to ${compareValue + percentage} over the previous ${numOfDaysInRange} days.`;
  return message;
}

export default {
  state,
  getters,
  mutations,
  actions,
};
